import React from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {IReduxStoreState} from '@src/@interfaces';
import classnames from '@src/helpers/classnames';
import ErrorBoundary from '@src/components/ErrorBoundary';
import NotificationsList from './NotificationsList';

type Props = ConnectedProps<typeof connector> & {
    footerRef?: React.RefObject<HTMLDivElement>,
    headerRef?: React.RefObject<HTMLDivElement>,
    containerStyle?: React.CSSProperties,
    children: React.ReactNode,
};
type State = {
    footerHeight?: number,
    headerHeight?: number,
};

export class ContentBlock extends React.Component<Props, State> {
    state: State = {};

    protected containerStyle: React.CSSProperties = {};
    protected headerChangesObserver?: MutationObserver;
    protected resizeTimeout?: ReturnType<typeof setTimeout>;

    private readonly contentRef: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);

        this.containerStyle = {
            ...this.containerStyle,
            ...(this.props.containerStyle ?? {}),
        };
        this.contentRef = React.createRef<HTMLDivElement>();
        this.onResize = this.onResize.bind(this);
    }

    componentDidMount() {
        if (super.componentDidMount) {
            super.componentDidMount();
        }

        if (window) {
            window.addEventListener('resize', this.onResize);
        }

        this.listenHeaderChanges();
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (super.componentDidUpdate) {
            super.componentDidUpdate(prevProps, prevState, snapshot);
        }

        if (prevProps.headerRef !== this.props.headerRef) {
            this.stopListeningHeaderChanges();
            this.listenHeaderChanges();
        }
    }

    componentWillUnmount() {
        if (super.componentWillUnmount) {
            super.componentWillUnmount();
        }

        clearTimeout(this.resizeTimeout);

        if (window) {
            window.removeEventListener('resize', this.onResize);
        }

        this.stopListeningHeaderChanges();
    }

    render() {
        return <div className={classnames('main-content', {
            'main-content--sidebar-collapsed': this.props.desktopSidebarSize === 'small',
            'main-content--mobile-sidebar-open': this.props.isMobileVersion && this.props.mobileSidebarIsOpen,
            'main-content--notifications-displayed': this.props.notificationsDisplayed,
        })} style={this.containerStyle}>
            <div className="main-content__backdrop"/>

            <div className="page-content">
                <div className="page-content__body" ref={this.contentRef}>
                    <ErrorBoundary>{this.props.children}</ErrorBoundary>
                </div>

                {this.props.notificationsDisplayed ? <NotificationsList
                        headerRef={this.props.headerRef}
                        contentRef={this.contentRef}
                        footerRef={this.props.footerRef}
                /> : null}
            </div>
        </div>;
    }

    protected onResize() {
        clearTimeout(this.resizeTimeout);

        this.resizeTimeout = setTimeout(() => {
            let footerHeight = undefined, headerHeight = undefined;

            if (this.props.footerRef?.current) {
                footerHeight = this.props.footerRef?.current.offsetHeight;
                this.containerStyle = {
                    ...this.containerStyle,
                    '--footer-height': `${footerHeight}px`
                } as React.CSSProperties;
            }

            if (this.props.headerRef?.current) {
                headerHeight = [].reduce.call(
                        this.props.headerRef?.current.children,
                        (res: any, node: HTMLElement) => {
                            if (node.hasAttribute('data-count-height')) {
                                res += node.offsetHeight;
                            }

                            return res;
                        },
                        this.props.headerRef?.current.offsetHeight ?? 0
                ) as any;
                this.containerStyle = {
                    ...this.containerStyle,
                    '--header-height': `${headerHeight}px`
                } as React.CSSProperties;
            }

            if (footerHeight === 0 && headerHeight === 0) {
                setTimeout(() => this.onResize(), 100);
            }

            this.setState({footerHeight, headerHeight});
        }, 10);
    }

    protected listenHeaderChanges() {
        if (!window || !('MutationObserver' in window) || !this.props.headerRef?.current || this.headerChangesObserver) {
            return;
        }

        this.headerChangesObserver = new MutationObserver(() => {
            setTimeout(() => this.onResize(), 1);
        });

        this.headerChangesObserver.observe(this.props.headerRef?.current, {
            childList: true,
            subtree: true,
        });
    }

    protected stopListeningHeaderChanges() {
        if (this.headerChangesObserver) {
            this.headerChangesObserver.disconnect();
            this.headerChangesObserver = undefined;
        }
    }
}

export const connector = connect((state: IReduxStoreState) => ({
    notificationsDisplayed: state.notifications.listDisplayed,
    desktopSidebarSize: state.theme.sidebar.desktop.size,
    isMobileVersion: state.theme.isMobileVersion,
    mobileSidebarIsOpen: state.theme.sidebar.mobile.open,
}));

export default connector(ContentBlock);
